import { Select } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './pf-select.module.css';
import { PfSelectTypeaheadField } from './pf-select-typeahead-field';
import type { PfSelectProps } from './pf-select.types';

export type {
  PfSelectOption,
  PfSelectPickerMode,
  PfSelectProps,
} from './pf-select.types';

const TYPEAHEAD_OPTIONS_LIMIT = 6;

const toOptionValue = (value: string | number | boolean) => String(value);

export function PfSelect({
  label,
  placeholder,
  options = [],
  value,
  defaultValue,
  onChange,
  onBlur,
  error,
  disabled,
  wrapperClassName,
  ariaLabel,
  pickerMode = 'dropdown',
  searchable,
}: PfSelectProps) {
  const { t } = useTranslation(['common'], { useSuspense: false });

  const [_value, handleChange] = useUncontrolled({
    value: value != null && value !== '' ? toOptionValue(value) : undefined,
    defaultValue:
      defaultValue != null && defaultValue !== ''
        ? toOptionValue(defaultValue)
        : undefined,
    onChange,
  });

  const selectData = useMemo(
    () =>
      options.map((option) => ({
        value: toOptionValue(option.value),
        label: option.label,
      })),
    [options]
  );

  const resolvedPlaceholder =
    placeholder ?? t('common:select_placeholder', { defaultValue: 'Sélectionner' });

  if (selectData.length > TYPEAHEAD_OPTIONS_LIMIT && pickerMode === 'modal') {
    return (
      <PfSelectTypeaheadField
        ariaLabel={ariaLabel}
        disabled={disabled}
        error={error}
        label={label}
        options={options}
        placeholder={resolvedPlaceholder}
        value={_value}
        wrapperClassName={wrapperClassName}
        onBlur={onBlur}
        onChange={handleChange}
      />
    );
  }

  return (
    <div className={classNames(styles.field, wrapperClassName)}>
      <Select
        aria-label={ariaLabel ?? label}
        className={styles.select}
        data={selectData}
        disabled={disabled}
        error={error}
        label={label}
        nothingFound={t('common:no_results')}
        placeholder={resolvedPlaceholder}
        searchable={searchable ?? selectData.length > 0}
        value={_value ?? null}
        withinPortal
        zIndex={400}
        onBlur={onBlur}
        onChange={(next) => {
          handleChange(next ?? '');
          onBlur?.();
        }}
      />
    </div>
  );
}

export default PfSelect;
